export type BillingCaseSummary = {
  id: string;
  organization_id: string;
  client_id: string;
  property_id: string | null;
  case_code: string;
  title: string;
  case_kind: "simple" | "project";
  status: "draft" | "active" | "completed" | "cancelled" | "archived";
  billing_strategy: "grouped" | "individual" | "milestone" | "progress";
  currency: string;
  billing_provider: string | null;
  contract_value_ex_vat: number | null;
  target_margin_rate_snapshot: number | null;
  latest_progress_version: number | null;
  latest_progress_method: string | null;
  latest_progress_percent: number | null;
  latest_approved_amount_ex_vat: number | null;
  expected_revenue_ex_vat: number;
  actual_revenue_ex_vat: number;
  expected_cost_ex_vat: number;
  actual_cost_ex_vat: number;
  expected_margin_ex_vat: number;
  actual_margin_ex_vat: number;
  invoiced_ex_vat: number;
  invoiced_inc_vat: number;
  paid_inc_vat: number;
  remaining_to_collect_inc_vat: number;
  remaining_to_invoice_ex_vat: number | null;
  updated_at: string;
};

export type BillingCaseLink = {
  id: string;
  billing_case_id: string;
  entity_type: "mission" | "quote" | "invoice" | "expense" | "payment";
  entity_id: string;
  link_role: string;
  label_snapshot: string | null;
  linked_at: string;
};

export type BillingCaseProgress = {
  id: string;
  billing_case_id: string;
  version: number;
  progress_method: "overall" | "amount" | "quantity" | "line" | "lot" | "phase";
  progress_percent: number | null;
  approved_amount_ex_vat: number | null;
  breakdown: Array<Record<string, unknown>>;
  status: "draft" | "approved" | "rejected";
  notes: string | null;
  approved_at: string | null;
  created_at: string;
};

export type BillingCaseLedgerEntry = {
  id: string;
  billing_case_id: string;
  direction: "revenue" | "cost";
  stage: "forecast" | "committed" | "actual" | "cancelled";
  category: string;
  description: string;
  quantity: number;
  unit: string | null;
  unit_amount_ex_vat: number;
  amount_ex_vat: number;
  vat_rate: number;
  expected_on: string | null;
  occurred_on: string | null;
  source_type: string | null;
  source_id: string | null;
  confidence: "low" | "medium" | "high" | "verified";
  supersedes_entry_id: string | null;
  created_at: string;
};

export type BillingCaseAmendment = {
  id: string;
  billing_case_id: string;
  amendment_number: number;
  title: string;
  reason: string;
  status: "pending_approval" | "approved" | "rejected" | "cancelled";
  previous_contract_value_ex_vat: number;
  change_amount_ex_vat: number;
  revised_contract_value_ex_vat: number;
  source_quote_id: string | null;
  created_at: string;
  decided_at: string | null;
  decision_note: string | null;
};

export type BillingCaseConsistencyFinding = {
  code: string;
  severity: "warning" | "blocking";
  title: string;
  detail: string;
  action: string;
  expected?: number;
  actual?: number;
};

export type BillingCaseConsistencyReview = {
  id: string;
  billing_case_id: string;
  review_number: number;
  severity: "clean" | "warning" | "blocking";
  warning_count: number;
  blocking_count: number;
  findings: BillingCaseConsistencyFinding[];
  contract_value_ex_vat: number | null;
  accepted_quote_total_ex_vat: number;
  invoiced_ex_vat: number;
  paid_inc_vat: number;
  latest_approved_amount_ex_vat: number | null;
  created_at: string;
};

export type BillingCaseOperationalSync = {
  id: string;
  billing_case_id: string;
  mission_id: string;
  mission_costing_review_id: string;
  sync_number: number;
  total_actual_cost_ex_vat: number;
  component_count: number;
  supersedes_sync_id: string | null;
  notes: string | null;
  created_at: string;
};

export type BillingCaseInvoicePreparation = {
  id: string;
  billing_case_id: string;
  invoice_id: string;
  preparation_number: number;
  document_kind: "progress" | "final" | "credit";
  source_progress_version: number | null;
  source_invoice_id: string | null;
  source_invoice_number: string | null;
  target_cumulative_ex_vat: number | null;
  prepared_amount_ex_vat: number;
  vat_rate: number;
  notes: string | null;
  created_at: string;
  invoice_number: string | null;
  invoice_status: string;
  issue_on: string | null;
  due_on: string | null;
  total_inc_vat: number;
};

export type BillingCaseInvoiceCandidate = {
  id: string;
  client_id: string;
  number: string | null;
  document_kind: "standard" | "deposit" | "progress" | "final" | "credit";
  parent_invoice_id: string | null;
  status: string;
  total_ex_vat: number;
  total_inc_vat: number;
};

export type BillingCaseCostReviewCandidate = {
  id: string;
  mission_id: string;
  review_number: number;
  status: "ready" | "needs_review" | "approved" | "rejected";
  actual_total_cost_ex_vat: number;
  decided_at: string | null;
};

export const billingCaseStatusLabels: Record<BillingCaseSummary["status"], string> = {
  draft: "Brouillon",
  active: "Actif",
  completed: "Terminé",
  cancelled: "Annulé",
  archived: "Archivé",
};

export const billingCaseKindLabels: Record<BillingCaseSummary["case_kind"], string> = {
  simple: "Dossier simple",
  project: "Projet / chantier",
};

export const billingStrategyLabels: Record<BillingCaseSummary["billing_strategy"], string> = {
  grouped: "Facture regroupée",
  individual: "Facture individuelle",
  milestone: "Par jalon",
  progress: "À l’avancement",
};

export const billingCaseLinkLabels: Record<BillingCaseLink["entity_type"], string> = {
  mission: "Mission",
  quote: "Devis",
  invoice: "Facture",
  expense: "Dépense",
  payment: "Règlement",
};

export const billingCaseAmendmentStatusLabels: Record<BillingCaseAmendment["status"], string> = {
  pending_approval: "À décider",
  approved: "Approuvé",
  rejected: "Refusé",
  cancelled: "Annulé",
};

export const billingCaseConsistencyLabels: Record<BillingCaseConsistencyReview["severity"], string> = {
  clean: "Cohérent",
  warning: "À contrôler",
  blocking: "Blocage détecté",
};

export function billingCaseProgressPercent(item: BillingCaseSummary) {
  if (item.latest_progress_percent != null)
    return Math.max(0, Math.min(100, Number(item.latest_progress_percent)));
  if (
    item.latest_approved_amount_ex_vat != null &&
    item.contract_value_ex_vat != null &&
    item.contract_value_ex_vat > 0
  )
    return Math.max(
      0,
      Math.min(
        100,
        (Number(item.latest_approved_amount_ex_vat) /
          Number(item.contract_value_ex_vat)) *
          100,
      ),
    );
  return 0;
}

export function billingCaseAttention(item: BillingCaseSummary) {
  const messages: string[] = [];
  const expectedRevenue = Number(item.expected_revenue_ex_vat || 0);
  const expectedMargin = Number(item.expected_margin_ex_vat || 0);
  const target = item.target_margin_rate_snapshot;
  const progress = billingCaseProgressPercent(item);
  const invoicedRate = item.contract_value_ex_vat && item.contract_value_ex_vat > 0
    ? (Number(item.invoiced_ex_vat || 0) / Number(item.contract_value_ex_vat)) * 100
    : null;

  if (item.contract_value_ex_vat == null)
    messages.push("Montant contractuel à compléter.");
  if (expectedMargin < 0)
    messages.push("La marge prévisionnelle est négative.");
  if (target != null && expectedRevenue > 0) {
    const marginRate = (expectedMargin / expectedRevenue) * 100;
    if (marginRate + 0.01 < Number(target))
      messages.push(
        `Marge prévisionnelle ${marginRate.toFixed(1)} % sous l’objectif ${Number(target).toFixed(1)} %.`,
      );
  }
  if (invoicedRate != null && progress > invoicedRate + 0.01)
    messages.push(
      `Avancement ${progress.toFixed(1)} % supérieur au niveau facturé ${invoicedRate.toFixed(1)} %.`,
    );
  if (Number(item.remaining_to_collect_inc_vat || 0) > 0)
    messages.push("Un montant facturé reste à encaisser.");
  return messages;
}
